import {
  mergeProps,
  createSignal,
  onCleanup,
  Show,
  type JSX,
  createEffect,
  createUniqueId,
} from "solid-js";
import { Portal } from "solid-js/web";

import { Icon } from "~/icon";
import clsx from "~/util/html/style";
import { createFloating, type Placement, type FallbackPlacements } from "~/util/html/floating";
import { hidePopover, showPopover } from "~/util/html/top_layer";

export type TooltipProps = {
  /**
   * A function is called only once the tooltip is up, so a panel that costs
   * something to build (an image preview) costs nothing on the triggers nobody
   * ever points at. Content passed as a value is built with its trigger, the
   * same as it always was.
   */
  content: JSX.Element | (() => JSX.Element);
  placement?: Placement;
  fallbackPlacements?: FallbackPlacements;
  enabled?: boolean;
  /**
   * "card" is the white panel a preview belongs on: images, pills and body copy
   * are unreadable on the dark chrome a phrase is written for.
   */
  variant?: "dark" | "card";
  children: (triggerProps: TriggerProps) => JSX.Element;
};

export type TriggerProps = {
  ref: (el: HTMLElement | SVGElement) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  // Focus mirrors hover so a tooltip on a focusable trigger is reachable by keyboard, not only by mouse.
  onFocus: () => void;
  onBlur: () => void;
  /** Undefined until a thunk's panel is up: an empty panel describes nothing. */
  "aria-describedby": string | undefined;
};

export function Tooltip(_props: TooltipProps) {
  const props = mergeProps(
    { placement: "top" as const, enabled: true, variant: "dark" as const },
    _props,
  );
  const [isTriggered, setIsTriggered] = createSignal(false);

  let triggerRef!: HTMLElement | SVGElement;
  let tooltipRef!: HTMLDivElement;
  let cleanup: (() => void) | null = null;

  // A thunk is content by definition and is left uncalled until it is shown;
  // anything else has to be there for the trigger to be worth pointing at.
  const hasContent = () => typeof props.content === "function" || Boolean(props.content);
  const enabled = () => props.enabled && hasContent();
  const content = () => (typeof props.content === "function" ? props.content() : props.content);
  const showing = () => isTriggered() && enabled();
  // Only a thunk waits for the trigger. A value is already built by the time it
  // arrives, so holding it back would buy nothing and would empty the panel
  // that every trigger points its `aria-describedby` at.
  const mounted = () => showing() || typeof props.content !== "function";

  createEffect(() => {
    if (!enabled()) {
      if (cleanup) {
        cleanup();
        cleanup = null;
      }
      return;
    }

    if (isTriggered() && !cleanup) {
      cleanup = createFloating({
        reference: triggerRef,
        floating: tooltipRef,
        config: {
          placement: props.placement,
          fallbackPlacements: props.fallbackPlacements,
        },
      });
      return;
    }

    if (!isTriggered() && cleanup) {
      cleanup();
      cleanup = null;
    }
  });

  // using popover api so tooltips render on top of dialog within the top layer
  createEffect(() => {
    if (showing()) showPopover(tooltipRef);
    else hidePopover(tooltipRef);
  });

  // A pointer raises the tooltip from wherever it is, native dialogs included,
  // so the escape that drops it is heard on the document. Preventing the default
  // only where it actually drops one is what keeps that escape out of the dialog
  // behind it. The event still travels: a layer under the panel has its own
  // escape to answer, and swallowing this one would take that away.
  createEffect(() => {
    if (!showing()) return;

    const dismiss = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setIsTriggered(false);
    };

    document.addEventListener("keydown", dismiss, true);
    onCleanup(() => document.removeEventListener("keydown", dismiss, true));
  });

  onCleanup(() => {
    if (cleanup) {
      cleanup();
    }
  });

  const tooltipId = createUniqueId();

  return (
    <>
      {props.children({
        ref: (el) => {
          triggerRef = el;
        },
        onMouseEnter: () => setIsTriggered(true),
        onMouseLeave: () => setIsTriggered(false),
        onFocus: () => setIsTriggered(true),
        onBlur: () => setIsTriggered(false),
        get "aria-describedby"() {
          return mounted() ? tooltipId : undefined;
        },
      })}

      <Portal>
        <div
          ref={tooltipRef}
          class={clsx(
            // The hidden rule is repeated from the `tooltip` utility rather than
            // shared with it: the utility is what plain `class="tooltip"` markup
            // and tippy's box carry, and the card variant never wears it.
            "pointer-events-none fixed z-floating-attachment max-w-5xl text-sm in-data-[disable-tooltip]:hidden",
            props.variant === "card"
              ? "rounded-lg border border-brand-subtle bg-white p-3 shadow-lg"
              : "tooltip",
          )}
          popover="manual"
          role="tooltip"
          id={tooltipId}
        >
          <Show when={mounted()}>{content()}</Show>
        </div>
      </Portal>
    </>
  );
}

// A tooltip on something that is not a control: a status icon, a badge, a mark
// that explains itself. The sentence is also the accessible name, so a screen
// reader reads it where a pointer would hover it, and nothing here takes the
// keyboard — a list carrying one of these per row would otherwise be a tab stop
// per row on the way to the controls that actually do something.
export function TooltipTrigger(props: {
  label: string;
  placement?: Placement;
  class?: string;
  children: JSX.Element;
}) {
  return (
    <Tooltip content={props.label} placement={props.placement}>
      {(trigger) => (
        <span
          {...trigger}
          role="img"
          aria-label={props.label}
          class={clsx("inline-flex", props.class)}
        >
          {props.children}
        </span>
      )}
    </Tooltip>
  );
}

// An info icon with a hover tooltip, for annotating labels and table headers
// inline (the same visual as DataTable's headerInfo).
export function InfoTooltip(props: { content: JSX.Element }) {
  return (
    <Tooltip content={<span class="block max-w-64">{props.content}</span>}>
      {(triggerProps) => (
        <Icon name="info" size="sm" class="inline-block align-text-bottom" {...triggerProps} />
      )}
    </Tooltip>
  );
}
